/**
 * Los errores de las RPC de focos, traducidos a algo que una persona pueda leer.
 *
 * Las RPC lanzan su código como MENSAJE (`FOCUS_NOT_FOUND`, `SEVERITY_REQUIRED`…),
 * y PostgREST lo devuelve tal cual en `error.message`. Aquí se decide qué se le
 * dice al usuario y, sobre todo, QUÉ HACE LA PANTALLA con cada uno:
 *
 *  · `retry`   — no es culpa de nadie (otra escritura tenía el candado): reintentar.
 *  · `refetch` — lo que se veía ya no es verdad (apareció un foco, otro lo cerró):
 *                volver a pedir los candidatos y dejar el diálogo abierto.
 *  · `legacy`  — los focos no existen para esta empresa o en esta base: seguir por
 *                el camino de siempre, sin decir nada.
 *  · `fix`     — falta algo en el formulario.
 *  · `stop`    — no se puede, y reintentar no lo arregla.
 */
export type FocusErrorKind = "retry" | "refetch" | "legacy" | "fix" | "stop";

export interface FocusError {
  code: string;
  kind: FocusErrorKind;
  /** Clave i18n del mensaje. */
  messageKey: string;
}

const KINDS: Record<string, FocusErrorKind> = {
  FOCUS_BUSY: "retry",
  FOCUS_CANDIDATES_CHANGED: "refetch",
  FOCUS_NOT_FOUND: "refetch",
  FOCUS_RESOLVED_TOO_OLD: "refetch",
  FOCUS_OTHER_CYCLE: "refetch",
  FOCUS_OTHER_BLOCK: "refetch",
  FOCUS_PEST_MISMATCH: "refetch",
  /* auditoría 23-09 ciclo#1: «Reabrir» un foco que ya no está cerrado (otro lo reabrió, doble
     toque, pantalla vieja). Lo que se veía ya no es verdad: se vuelve a pedir la ficha. */
  FOCUS_NOT_RESOLVED: "refetch",
  FOCI_DISABLED: "legacy",
  SEVERITY_REQUIRED: "fix",
  PEST_REQUIRED: "fix",
  PEST_NOT_FOUND: "fix",
  INVALID_VERDICT: "fix",
  VERDICT_GONE_REQUIRES_CLEAN_CHECK: "fix",
  SESSION_NOT_FOUND: "stop",
  SESSION_DISMISSED: "stop",
  SESSION_NOT_CLASSIFIED: "stop",
  SESSION_WITHOUT_BLOCK: "stop",
  PERMISSION_DENIED: "stop",
  OUT_OF_SCOPE: "stop",
  AUTH_REQUIRED: "stop",
  MERGE_SAME_FOCUS: "stop",
  SPLIT_WOULD_EMPTY_SOURCE: "stop",
  SPLIT_SESSIONS_NOT_IN_FOCUS: "stop",
  FOCUS_FIELDS_READONLY: "stop",
};

/** Varios códigos, un mismo mensaje: a quien confirma le da igual cuál de los seis «ya no es verdad» fue. */
const MESSAGE: Record<string, string> = {
  FOCUS_BUSY: "busy",
  SEVERITY_REQUIRED: "severity",
  PEST_REQUIRED: "pest",
  PEST_NOT_FOUND: "pest",
  SPLIT_WOULD_EMPTY_SOURCE: "splitEmpty",
  PERMISSION_DENIED: "forbidden",
  OUT_OF_SCOPE: "forbidden",
  AUTH_REQUIRED: "forbidden",
  FOCUS_FIELDS_READONLY: "forbidden",
};
const keyOf = (code: string, kind: FocusErrorKind) =>
  `pests.scouting.focusLink.errors.${MESSAGE[code] ?? (kind === "refetch" ? "changed" : "generic")}`;

export function focusErrorOf(error: unknown): FocusError {
  const e = (error ?? {}) as { message?: string; code?: string };
  // 55P03 = lock_timeout; PGRST202 = la función no existe (base sin migrar).
  if (e.code === "55P03") return { code: "FOCUS_BUSY", kind: "retry", messageKey: keyOf("FOCUS_BUSY", "retry") };
  if (e.code === "PGRST202") return { code: "FOCI_DISABLED", kind: "legacy", messageKey: keyOf("FOCI_DISABLED", "legacy") };
  const code = (e.message ?? "").trim().split(/\s/)[0];
  const kind = KINDS[code] ?? "stop";
  return { code: code || "UNKNOWN", kind, messageKey: keyOf(code, kind) };
}

/** Las claves de mensaje que existen: el test de paridad comprueba que están en los nueve idiomas. */
export const FOCUS_ERROR_MESSAGE_KEYS = ["busy", "changed", "severity", "pest", "splitEmpty", "forbidden", "generic"] as const;
